import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { getContext, useCombinedRefs } from 'common';
import { CONSTANTS } from 'common/ListKeyboardNav';
import useHover from 'common/useHover';
import { CCFKComponentProps } from 'common/types';
import { ListContext } from './List';
import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;

const ListItemBasicContext = React.createContext<any>({});

export interface ListItemBasicProps extends Partial<CCFKComponentProps> {
  /** Focus this item on mount */
  autoFocus?: boolean;
  /** Display the list item as disabled. */
  disabled?: boolean;
  /** If true, a bottom border is added to the list item. */
  hasBottomBorder?: boolean;
  /** Function triggered when the list item is hovered. */
  hoverCallback?: () => void;
  /** Callback function that is fired when the list item is selected (by clicking w/mouse or enter/space key via keyboard navigation).
   * where eventObj={type, event}:
   *   - type: 'onClick'
   *   - event: event.nativeEvent
  */
  onClick?: React.MouseEventHandler;
  /** Callback function that is fired when a key is pressed.  event is returned. */
  onKeyDown?: React.KeyboardEventHandler;
  /** Display the list item as selected. */
  selected?: boolean;
  /** Object indicating styles to be used for the ListItem. */
  style?: React.CSSProperties;
  /** The list items, consider using `ListItemText`, `ListItemIcon`. */
  children: React.ReactNode;
  /** Place focus on this item. */
  focusItem?: boolean;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** All other props will be provided to the root element. */
const ListItemBasic = React.forwardRef<HTMLElement, ListItemBasicProps>((props: ListItemBasicProps, forwardedRef) => getContext(({ RTL }) => {
  const {
    children,
    autoFocus,
    disabled,
    selected,
    hasBottomBorder,
    style,
    onKeyDown,
    onFocus,
    onBlur,
    onClick,
    hoverCallback,
    focusItem,
    ...otherProps
  } = props;
  const context = useContext(ListContext);
  const [hoverRef, isHovered] = useHover();
  const [isFocused, setIsFocused] = useState(false);
  const innerRef = useRef(null);
  const ref = useCombinedRefs(forwardedRef, hoverRef, innerRef);

  useEffect(() => {
    autoFocus && !disabled && ref && ref.current.focus();
  }, []); // autoFocus on mount only

  useEffect(() => {
    if (focusItem === true) {
      innerRef && innerRef.current && innerRef.current.focus();
    }
  }, [focusItem]);

  useEffect(() => {
    if (isHovered) {
      hoverCallback && hoverCallback();
    }
  }, [isHovered]);
  const handleOnKeyDown = (event) => {
    event.persist();
    onKeyDown && onKeyDown(event);
  };

  const handleOnClick = (event) => {
    event.persist();
    onClick && onClick(event);
  };

  const handleOnFocus = (event) => {
    setIsFocused(true);
    event.preventDefault();
    onFocus && onFocus(event);
  };

  const handleOnBlur = (event) => {
    setIsFocused(false);
    onBlur && onBlur(event);
  };

  const classes = classnames({
    [`${LIST_ITEM_TABABLE_ITEM}-${context.uniqueListId}`]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });
  return (
    <Styled.ListItemBasic
      className={classes}
      aria-disabled={disabled}
      aria-selected={selected}
      ref={ref}
      RTL={RTL}
      $theme={context.theme}
      style={style}
      tabIndex={-1}
      disabled={disabled}
      selected={selected}
      hasBottomBorder={hasBottomBorder}
      hoverEffect={context.hoverEffect}
      onFocus={handleOnFocus}
      onKeyDown={handleOnKeyDown}
      onClick={handleOnClick}
      onBlur={handleOnBlur}
      {...otherProps}
    >
      <ListItemBasicContext.Provider value={{ disabled, selected, isHovered, isFocused, hoverEffect: context.hoverEffect }}>
        {children}
      </ListItemBasicContext.Provider>
    </Styled.ListItemBasic>
  );
}));

ListItemBasic.displayName = 'ListItemBasic';

ListItemBasic.defaultProps = {
  onClick: undefined,
  onKeyDown: undefined,
  disabled: false,
  selected: false,
  hasBottomBorder: false,
  hoverCallback: undefined,
  style: undefined,
  autoFocus: false,
  focusItem: false,
  onFocus: undefined,
  onBlur: undefined
};

export { ListItemBasic as default, ListItemBasicContext };
